import { type FC, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';

import { trackEvent } from 'dibs-tracking';
import { useFavoritesItem } from 'dibs-portfolio-folder/exports/useFavoritesItem.tsx';
import { SharedFavoriteItem } from '../../components/global/FavoriteIcons/SharedFavoriteItem.tsx';
import {
    BuyerProductCarousel,
    SRC_SET_SIZES_TILE,
} from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';

import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { useBuyerProductTileSize } from '../hooks/useBuyerProductTileSize.ts';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';

import { type ItemShippingPrices } from '../../finding/hooks/useItemShippingPrices.ts';
import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps.ts';
import { type MeasurementUnit } from 'dibs-intl/exports/units';
import { type SharedItemTile_viewer$key } from './__generated__/SharedItemTile_viewer.graphql.ts';
import { type SharedItemTile_itemSearch$key } from './__generated__/SharedItemTile_itemSearch.graphql.ts';
import { type SharedItemTile_item$key } from './__generated__/SharedItemTile_item.graphql.ts';
import { type SharedItemTile_user$key } from './__generated__/SharedItemTile_user.graphql.ts';
import { getShippingCostSbVariant } from '../../utils/abTestHelper.ts';

const itemSearchFragment = graphql`
    fragment SharedItemTile_itemSearch on ItemSearchQueryConnection {
        ...BuyerProductCarousel_itemSearch
    }
`;
const itemFragment = graphql`
    fragment SharedItemTile_item on Item
    @argumentDefinitions(
        photosLimit: { type: "Int", defaultValue: 1 }
        fetchVideo: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        priceBookName: { type: "String" }
        showSeller: { type: "Boolean!" }
        isTrade: { type: "Boolean" }
    ) {
        ...BuyerProductCarousel_item
            @arguments(
                isTrade: $isTrade
                fetchVideo: $fetchVideo
                pageDisplayEnum: $pageDisplayEnum
                priceBookName: $priceBookName
                showSeller: $showSeller
            )
        ...SharedFavoriteItem_item
        ...useFavoritesItem_item
        serviceId
        productPhotos: photos(limit: $photosLimit) {
            smallPath
            versions {
                webPath
            }
        }
    }
`;

const userFragment = graphql`
    fragment SharedItemTile_user on User
    @argumentDefinitions(showMerchandizingTool: { type: "Boolean" }) {
        ...useFavoritesItem_user
        ...BuyerProductCarousel_user
            @arguments(showSeller: $showSeller, showMerchandizingTool: $showMerchandizingTool)
    }
`;

const viewerFragment = graphql`
    fragment SharedItemTile_viewer on Viewer {
        ...BuyerProductCarousel_viewer
    }
`;

type Props = {
    viewer: SharedItemTile_viewer$key | null | undefined;
    itemSearch: SharedItemTile_itemSearch$key | null | undefined;
    item: SharedItemTile_item$key | null;
    user: SharedItemTile_user$key | null | undefined;
    favorites?: FavoritesProviderChildrenProps;
    isSponsored?: boolean;
    index: number;
    isOpenPdpInNewTabVariant?: boolean;
    isVisuallySimilarEnabled?: boolean;
    lazyLoadStartIndex?: number;
    imageLoadVerticalOffset?: number;
    onClick?: () => void;
    showMeasurements?: boolean;
    measurementUnit?: MeasurementUnit;
    beforeRender?: () => void;
    onContentVisible?: () => void;
    showSwipeIndicator?: boolean;
    fetchFolderOnMouseOver?: boolean;
    showFavoritesTooltip?: boolean;
    showQuickView?: boolean;
    showMSKUSwatch?: boolean;
    showShippingBadge?: boolean;
    urgencySignal?: string | null;
    shippingPrices?: ItemShippingPrices | null;
    hideRecentSoldPrice?: boolean;
    hideTile?: boolean;
};

export const SharedItemTile: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    item: itemRef,
    user: userRef,
    isSponsored = false,
    favorites,
    index,
    isOpenPdpInNewTabVariant,
    isVisuallySimilarEnabled,
    lazyLoadStartIndex = 2,
    onClick,
    showMeasurements,
    measurementUnit,
    imageLoadVerticalOffset,
    beforeRender,
    onContentVisible,
    showSwipeIndicator,
    fetchFolderOnMouseOver,
    showFavoritesTooltip,
    showQuickView,
    showMSKUSwatch,
    showShippingBadge = true,
    urgencySignal = null,
    shippingPrices,
    hideRecentSoldPrice = false,
    hideTile,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const tileElementRef = useRef<HTMLDivElement | null>(null);
    const itemId = item?.serviceId || '';
    const afterDisplay = useSbSelector(
        state =>
            state.relayVariables.variables.afterDisplay || state.relayVariables.variables.isClient
    );
    const {
        isMobile,
        isSsrOnlyBot = false,
        urgencySignalsMwVariant,
        isShipsFromSBTileVariant,
    } = useServerVarsContext();
    const { currency, showCurrency } = useCurrency();

    const showUrgencySignal = urgencySignalsMwVariant === 'variant' || !isMobile;
    const isShippingCostSbVariant = afterDisplay && getShippingCostSbVariant() === 'variant';
    const tileSize = useBuyerProductTileSize();
    const {
        isFolderVisible,
        onUnFavorited,
        onFolderActionStart,
        onFolderActionEnd,
        onMouseEnter,
        onMouseLeave,
        showFolderPlusIcon,
    } = useFavoritesItem({
        item,
        user,
        applyMouseEvents: fetchFolderOnMouseOver,
        tileElementRef,
    });

    function onQuickViewDetailsClick(): void {
        trackEvent({
            category: 'ecommerce',
            label: itemId,
            action: 'product detail view - from seo quicklook',
        });
    }

    const photo = item?.productPhotos?.[0];
    if (!photo?.smallPath && !photo?.versions?.[0]?.webPath) {
        return null;
    }

    return (
        <BuyerProductCarousel
            viewer={viewer}
            itemSearch={itemSearch}
            item={item}
            user={user}
            afterDisplay={afterDisplay}
            beforeRender={beforeRender}
            currency={currency}
            imageBackgroundColor="moonStone"
            imageLoadVerticalOffset={imageLoadVerticalOffset}
            index={index}
            isSponsored={isSponsored}
            itemsPerRow={lazyLoadStartIndex}
            tileElementRef={tileElementRef}
            hideRecentSoldPrice={hideRecentSoldPrice}
            hideTile={hideTile}
            linkTarget={isOpenPdpInNewTabVariant ? '_blank' : '_self'} // 'open_pdp_in_new_tab' abTest is configured to run only on desktop
            onClick={onClick}
            onContentVisible={onContentVisible}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            measurementUnit={measurementUnit}
            urgencySignal={showUrgencySignal ? urgencySignal : null}
            shippingPrices={shippingPrices}
            showBrowseSimilar={isVisuallySimilarEnabled && afterDisplay}
            showMeasurements={showMeasurements}
            showPrice={(afterDisplay && showCurrency) || isSsrOnlyBot}
            showSwipeIndicator={showSwipeIndicator}
            showMskuSwatch={showMSKUSwatch}
            showShippingCost={
                showShippingBadge && afterDisplay && showCurrency && !isShipsFromSBTileVariant
            }
            hasQuickView={showQuickView}
            showQuickViewButton={!!afterDisplay}
            onQuickViewDetailsClick={onQuickViewDetailsClick}
            size={tileSize}
            srcSet={{ sizes: SRC_SET_SIZES_TILE }}
            favoritesComponent={
                <SharedFavoriteItem
                    isFolderVisible={isFolderVisible}
                    onUnFavorited={onUnFavorited}
                    onFolderActionStart={onFolderActionStart}
                    onFolderActionEnd={onFolderActionEnd}
                    showFolderPlusIcon={showFolderPlusIcon}
                    item={item}
                    isSponsored={isSponsored}
                    favorites={favorites}
                    fetchFolderOnMouseOver={fetchFolderOnMouseOver}
                    showFavoritesTooltip={showFavoritesTooltip}
                    tileSize={tileSize}
                />
            }
            isShippingCostSbVariant={isShippingCostSbVariant}
        />
    );
};
